import {
	faCheck,
	faChevronLeft,
	faEye,
	faTimes,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import * as React from "react";
import { useState } from "react";
import { store } from "react-notifications-component";
import ReactPaginate from "react-paginate";
import { connect } from "react-redux";
import { useHistory } from "react-router-dom";
import Select from "react-select";
import { CardLink, Col, FormGroup, Input, Row, Spinner } from "reactstrap";
import { Dispatch } from "redux";
import { testimonialAction } from "../../actions/testimonialAction";
import { videoHotlineAction } from "../../actions/videoHotlineAction";
import updownArrows from "../../assets/images/up_down_arrows.svg";
import { getLoggedUserId } from "../../services/selectors/Accounts";
import { showAlert } from "../common/functions";

interface TestimonialProps {
	dispatch: Dispatch<any>;
	testimonialData: any;
	videoHotlineData: any;
}

const TestimonialList: React.SFC<TestimonialProps> = ({
	dispatch,
	testimonialData,
	videoHotlineData,
}) => {
	const history = useHistory();
	const [selectedId, setSelectedId] = useState(-1);
	const [showModal, setShowModal] = useState(false);
	const [filterInput, setFilterInput] = useState(null);
	const [currentPage, setCurrentPage] = useState<number>(1);
	const [searchText, setSearch] = useState<string>("");
	const [debouncedSearch, setDebouncedSearch] = useState<string>("");
	const [columnSort, setColumnSort] = useState<any>(null);

	React.useEffect(() => {
		const handler = setTimeout(() => {
			setCurrentPage(1);
			setDebouncedSearch(searchText);
		}, 400);
		return () => {
			clearTimeout(handler);
		};
	}, [searchText]);

	const handleChangeStatus = (status) => {
		setFilterInput(status);
		const body = {
			sortDirection: columnSort?.direction ?? "",
			sortColumn: columnSort?.column ?? "",
			pageNumber: 1,
			pageSize: 25,
			searchText: debouncedSearch,
			messageBoardId: videoHotlineData?.message?.messageBoardId,
			status: status,
		};
		setShowModal(true);
		setCurrentPage(1);
		dispatch(testimonialAction.testimonialGetByFilter(body));
	};

	if (
		testimonialData &&
		!testimonialData.isLoading &&
		testimonialData.isListSuccess
	) {
		testimonialData.isListSuccess = false;
		if (showModal) {
			setShowModal(false);
		}
	}

	if (
		selectedId >= 0 &&
		testimonialData.isSuccess &&
		testimonialData.isDeleteCompleted &&
		!testimonialData.isError
	) {
		showAlert(testimonialData.status, false);
		testimonialData.isSuccess = false;
		testimonialData.isDeleteCompleted = false;
		const body = {
			sortDirection: columnSort?.direction ?? "",
			sortColumn: columnSort?.column ?? "",
			pageNumber: currentPage,
			pageSize: 25,
			searchText: debouncedSearch,
			messageBoardId: videoHotlineData?.message?.messageBoardId,
			status: filterInput,
		};
		dispatch(testimonialAction.testimonialGetByFilter(body));
		setSelectedId(-1);
	}
	if (!testimonialData.isSuccess && testimonialData.isError) {
		if (testimonialData.status === "Unauthorized") {
			history.push("/401");
		} else {
			showAlert(testimonialData.status, true);
		}
		testimonialData.isError = false;
		setSelectedId(-1);
		if (showModal) {
			setShowModal(false);
		}
	}

	React.useEffect(() => {
		if (!videoHotlineData?.message?.messageBoardId) {
			dispatch(videoHotlineAction.getMessageBoardID("testimonial"));
		}
		if (!!videoHotlineData?.message?.messageBoardId) {
			const body = {
				sortDirection: columnSort?.direction ?? "",
				sortColumn: columnSort?.column ?? "",
				pageNumber: currentPage,
				pageSize: 25,
				searchText: debouncedSearch,
				messageBoardId: videoHotlineData?.message?.messageBoardId,
				status: filterInput,
			};
			dispatch(testimonialAction.testimonialGetByFilter(body));
		}
		setShowModal(true);
	}, [videoHotlineData?.message?.messageBoardId, debouncedSearch, columnSort]);

	React.useEffect(() => {
		if (testimonialData?.isUpdateStatus) {
			const body = {
				sortDirection: columnSort?.direction ?? "",
				sortColumn: columnSort?.column ?? "",
				pageNumber: currentPage,
				pageSize: 25,
				searchText: debouncedSearch,
				messageBoardId: videoHotlineData?.message?.messageBoardId,
				status: filterInput,
			};
			dispatch(testimonialAction.testimonialGetByFilter(body));
			const alertMessage = testimonialData?.status;
			if (alertMessage && alertMessage != "") {
				store.addNotification({
					title: "Success!",
					message: alertMessage,
					type: "success",
					insert: "top",
					container: "top-right",
					animationIn: ["animate__animated", "animate__fadeIn"],
					animationOut: ["animate__animated", "animate__fadeOut"],
					dismiss: {
						duration: 5000,
						onScreen: true,
					},
				});
				testimonialData.isUpdateStatus = false;
				window.localStorage.setItem("alertMessage", "");
			}
		}
	}, [testimonialData?.isUpdateStatus]);

	const approveVideo = async (id: number) => {
		try {
			const data = {
				adminUserId: parseInt(getLoggedUserId()),
				messageId: id,
				isApproved: true,
			};
			setShowModal(true);
			await dispatch(testimonialAction.updateTestimonialStatus(data));
		} catch (error) {
			console.error("Failed to approve testimonial:", error);
		}
	};

	const rejectVideo = async (id: number) => {
		try {
			const data = {
				adminUserId: parseInt(getLoggedUserId()),
				messageId: id,
				isApproved: false,
			};
			setShowModal(true);
			await dispatch(testimonialAction.updateTestimonialStatus(data));
		} catch (error) {
			console.error("Failed to reject testimonial:", error);
		}
	};

	const handleActiveTestimonial = async (id: any, status: any) => {
		const data = {
			adminUserId: parseInt(getLoggedUserId()),
			messageId: id,
			isActive: !status,
		};
		setShowModal(true);
		await dispatch(testimonialAction.updateTestimonialVideoStatus(data));
	};
	const videoEnhanceStatus = (status: any) => {
		switch (status) {
			case 0:
				return "Enhancement in Progress";
			case 1:
				return "Enhanced Video";
			case 2:
				return "Original Video";
			default:
				return "Original Video";
		}
	};
	const getColorByVideoStatus = (status: any) => {
		switch (status) {
			case 0:
				return "#FFC300";
			case 1:
				return "#008001";
			case 2:
				return "#007bff";
			default:
				return "#007bff";
		}
	};
	function getUserFullName(data) {
		const fullName = `${data?.messageDTO?.createdByUser?.firstName ?? ""} ${
			data?.messageDTO?.createdByUser?.lastName ?? ""
		}`.trim();
		const maxLength = 90;
		return fullName.length > maxLength
			? fullName.slice(0, maxLength) + "…"
			: fullName;
	}

	const getColorByStatus = (status: any) => {
		switch (status) {
			case 0:
				return "#FFC300";
			case 1:
				return "#008001";
			case 2:
				return "#FF0000";
			default:
				return "#000000";
		}
	};

	const getConversionStatus = (key: any) => {
		switch (key) {
			case 0:
				return "InProgress";
			case 1:
				return "Completed";
			case 2:
				return "Failed";
			default:
				return "-";
		}
	};

	const handleSort = (sortColumn) => {
		const direction =
			sortColumn === columnSort?.column
				? columnSort?.direction === "desc"
					? "asc"
					: "desc"
				: "desc";
		setColumnSort({ column: sortColumn, direction: direction });
	};
	const handleGetSortColumn = (display, value) => {
		return (
			<div
				className="column-div d-flex"
				onClick={(e) => {
					handleSort(value);
				}}
			>
				{display}
				<img src={updownArrows} alt="updownArrows" />
			</div>
		);
	};

	function getEngagementStatus(value: any): JSX.Element {
		if (value === undefined || value === null) {
			const status = "Unknown";
			const cls = status.toLowerCase();
			return (
				<div className="d-flex align-items-center justify-content-center gap-1">
					<span className={`retention-percent ${cls}`}>—</span>
					<span className={`retention-badge ${cls}`}>N/A</span>
				</div>
			);
		}

		const n = Number(value);
		if (Number.isNaN(n)) {
			const status = "Unknown";
			const cls = status.toLowerCase();
			return (
				<div className="d-flex align-items-center justify-content-center gap-1">
					<span className={`retention-percent ${cls}`}>—</span>
					<span className={`retention-badge ${cls}`}>N/A</span>
				</div>
			);
		}

		const clamped = Math.max(0, Math.min(100, n));
		const rounded = Math.round(clamped);

		let status: "Low" | "Medium" | "High" = "High";

		if (clamped < 30) {
			status = "Low";
		} else if (clamped < 60) {
			status = "Medium";
		} else {
			status = "High";
		}

		const displayPercent = rounded;
		const cls = status.toLowerCase();

		return (
			<div className="d-flex align-items-center justify-content-center gap-1">
				<span className={`retention-percent ${cls}`}>
					{displayPercent !== null ? `${displayPercent}%` : "—"}
				</span>
				<span className={`retention-badge ${cls}`}>{status}</span>
			</div>
		);
	}

	return (
		<div data-testid="testimonialPage" className="testimonial-page">
			<div className="card-box-link-back-div">
				<FontAwesomeIcon icon={faChevronLeft} />
				<CardLink
					onClick={() => history.push("/")}
					className="card-box-link-back"
					data-testid="bible-backtolist"
				>
					Back to List
				</CardLink>
			</div>
			<div className="mb-3">
				<Row className="d-flex justify-content-end">
					<Col lg="4" className="d-flex gap-3">
						<Select
							id="statusSelect"
							className="status-select"
							classNamePrefix="rs"
							placeholder="Select status"
							isClearable
							options={[
								{ value: 1, label: "Approved" },
								{ value: 2, label: "Pending" },
								{ value: 0, label: "Rejected" },
							]}
							onChange={(selected) => {
								if (selected) {
									handleChangeStatus(selected.value);
								} else {
									handleChangeStatus(null);
								}
							}}
							styles={{
								container: (base) => ({
									...base,
									minWidth: 180,
									flexShrink: 0,
								}),
								control: (base) => ({ ...base, minWidth: 180 }),
								clearIndicator: (base) => ({ ...base, cursor: "pointer" }),
							}}
						/>
						<Input
							type="search"
							onChange={(event) => setSearch(event.target.value)}
							className="form-control"
							id="search-filter"
							placeholder="Search here..."
							value={searchText}
						/>
					</Col>
				</Row>
			</div>
			<div className="testimonial-table">
				<table className="table table-hover" data-testid="testimonialTable">
					<thead data-testid="testimonialTableHead">
						<tr>
							<th scope="col" data-testid="id-th">
								#
							</th>
							<th scope="col" data-testid="author-th">
								Name
							</th>
							<th scope="col" data-testid="date-th">
								Created Date
							</th>
							<th scope="col" data-testid="enhance-th">
								Video Status
							</th>
							<th scope="col" data-testid="conversion-status-th">
								Conversion Status
							</th>
							<th scope="col" data-testid="total-click-th">
								{handleGetSortColumn("Total Clicks", "totalclicks")}
							</th>
							<th scope="col" data-testid="unique-click-th">
								{handleGetSortColumn(
									"Unique clicks per user",
									"uniqueusersclicked"
								)}
							</th>
							<th scope="col" data-testid="order-th">
								{handleGetSortColumn("Retention Rate", "avgretentionratio")}
							</th>
							<th scope="col" data-testid="videourl-th">
								Video
							</th>
							<th scope="col" data-testid="action-th">
								Action
							</th>
							<th scope="col" data-testid="status-th">
								Status
							</th>
						</tr>
					</thead>
					<tbody data-testid="testimonialTableBody">
						{showModal ? (
							<tr>
								<td colSpan={11} className="text-center">
									<Spinner />
								</td>
							</tr>
						) : (
							<>
								{testimonialData &&
									testimonialData?.items?.allMessages?.map((data: any) => (
										<>
											<tr key={data?.messageDTO?.messageId}>
												<th scope="row" className="icon-column">
													<a
														href={
															"/testimonial?id=" + data?.messageDTO?.messageId
														}
														className="detail-link"
														data-testid="testimonialDetailButton"
													>
														# {data?.messageDTO?.messageId}
													</a>
												</th>
												<td id="authorName">{getUserFullName(data)}</td>
												<td id="date">
													{new Date(
														data?.messageDTO?.createdAtUtc
													).toLocaleDateString()}
												</td>
												<td id="enhanced">
													<span
														style={{
															color: getColorByVideoStatus(
																data?.messageDTO?.messageAttachments?.[0]
																	?.videoEnhanceStatus
															),
														}}
													>
														{videoEnhanceStatus(
															data?.messageDTO?.messageAttachments?.[0]
																?.videoEnhanceStatus
														)}
													</span>
												</td>
												<td id="conversion-status">
													<span
														style={{
															color: getColorByStatus(
																data?.messageDTO?.messageAttachments?.[0]
																	?.hlsStatus
															),
														}}
													>
														{getConversionStatus(
															data?.messageDTO?.messageAttachments?.[0]
																?.hlsStatus
														)}
													</span>
												</td>
												<td id="total-clicks" className="text-center">
													{data?.messageDTO?.totalClicks}
												</td>
												<td id="unique-clicks" className="text-center">
													{data?.messageDTO?.uniqueUsersClicked}
												</td>
												<td id="retention-rate" className="text-center">
													{getEngagementStatus(
														data?.messageDTO?.avgRetentionRatio
													)}
												</td>
												<td id="videourl" className="text-center">
													<CardLink
														className="card-box-link-title"
														href={
															"/testimonial?id=" + data?.messageDTO?.messageId
														}
														title={
															data?.messageDTO?.messageAttachments?.messageId
														}
													>
														<FontAwesomeIcon
															className="footer-box-icon"
															icon={faEye}
															data-bs-toggle="tooltip"
															data-bs-placement="bottom"
															title="View video"
														/>
													</CardLink>
												</td>
												<td id="action" className="text-center">
													{data?.messageDTO?.isApproved === true ? (
														<span
															style={{ color: "green", fontWeight: "bold" }}
														>
															Approved
														</span>
													) : data?.messageDTO?.isApproved === false ? (
														<span style={{ color: "red", fontWeight: "bold" }}>
															Rejected
														</span>
													) : (
														<div className="d-flex gap-3 justify-content-center">
															{data?.messageDTO?.messageAttachments?.[0]
																?.videoEnhanceStatus === 0 ? (
																<span>-</span>
															) : (
																<>
																	<FontAwesomeIcon
																		icon={faCheck}
																		title="Approve"
																		className="approve-btn"
																		onClick={() =>
																			approveVideo(data?.messageDTO?.messageId)
																		}
																	/>
																	<FontAwesomeIcon
																		icon={faTimes}
																		title="Reject"
																		className="reject-btn"
																		onClick={() =>
																			rejectVideo(data?.messageDTO?.messageId)
																		}
																	/>
																</>
															)}
														</div>
													)}
												</td>
												<td id="publish" className="text-center">
													{data?.messageDTO?.messageAttachments?.[0]
														?.hlsStatus === 1 && (
														<FormGroup switch>
															<Input
																type="switch"
																className="active-switch"
																id="activeInactiveSwitch"
																checked={data?.messageDTO?.isActive}
																onChange={() =>
																	handleActiveTestimonial(
																		data?.messageDTO?.messageId,
																		data?.messageDTO?.isActive
																	)
																}
															/>
														</FormGroup>
													)}
												</td>
											</tr>
										</>
									))}
								{testimonialData &&
									testimonialData?.items?.allMessages.length <= 0 && (
										<tr className="no-data">
											<th scope="row" colSpan={11}>
												<h4> No data found </h4>
											</th>
										</tr>
									)}
							</>
						)}
					</tbody>
				</table>
			</div>
			<div className="pagination">
				<ReactPaginate
					previousLabel={"<<"}
					nextLabel={">>"}
					breakLabel={"..."}
					pageCount={Math.ceil(+testimonialData?.items?.totalCount / 25)}
					marginPagesDisplayed={2}
					pageRangeDisplayed={3}
					onPageChange={(data) => {
						setCurrentPage(data.selected + 1);
						setShowModal(true);
						const body = {
							sortDirection: columnSort?.direction ?? "",
							sortColumn: columnSort?.column ?? "",
							pageNumber: data.selected + 1,
							pageSize: 25,
							searchText: debouncedSearch,
							messageBoardId: videoHotlineData?.message?.messageBoardId,
							status: filterInput,
						};
						dispatch(testimonialAction.testimonialGetByFilter(body));
					}}
					containerClassName={"pagination"}
					pageClassName={"page-item"}
					previousClassName={"page-item"}
					breakClassName={"page-item"}
					breakLinkClassName={"page-link"}
					nextClassName={"page-item"}
					pageLinkClassName={"page-link"}
					previousLinkClassName={"page-link"}
					nextLinkClassName={"page-link"}
					activeClassName={"active"}
					forcePage={currentPage - 1}
				/>
			</div>
		</div>
	);
};

const mapStateToProps = (state: any) => {
	const { testimonialData, videoHotlineData } = state;
	return {
		testimonialData,
		videoHotlineData,
	};
};

export default connect(mapStateToProps)(TestimonialList);
